// covered-by: qa/rickrubin/tests/specs/cca/ask.puntt.notifications.settings.spec.ts
import {
  defaultAskPunttNotificationPrefs,
  type AskPunttNotificationPrefs,
  type StoredAskPunttNotificationPrefs,
} from "@mg/schemas/src/commons/askPunttNotificationPrefs";
import { Card, Flex, Heading, Switch, Text } from "@radix-ui/themes";
import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import { AskPunttDesktopNotificationsToggleControl } from "./lazyAskPunttSettingsWidgets";

import { useUI } from "../../../contexts/ui";
import {
  getUserProfile,
  updateCurrentUserProfile,
} from "../../../services/userProfile";

type EmailPrefKey = keyof AskPunttNotificationPrefs;

const EMAIL_PREF_ROWS: {
  key: EmailPrefKey;
  label: string;
  description: string;
  testId: string;
}[] = [
  {
    key: "emailMention",
    label: "Mentions",
    description:
      "Email when someone @mentions you in an Ask Puntt chat or project.",
    testId: "settings-notifications-email-mention",
  },
  {
    key: "emailActionNeeded",
    label: "Action needed",
    description:
      "Email when you are assigned a new to-do or action item. The message includes a link to that item.",
    testId: "settings-notifications-email-action-needed",
  },
  {
    key: "emailNewMessage",
    label: "New chat messages",
    description:
      "Email when Ask Puntt posts a new assistant reply or a teammate sends a message in a shared chat.",
    testId: "settings-notifications-email-new-message",
  },
  {
    key: "emailReviewComplete",
    label: "Review complete",
    description: "Email when an artwork review finishes in a project or chat.",
    testId: "settings-notifications-email-review-complete",
  },
  {
    key: "emailArtifactReady",
    label: "Artifact ready",
    description:
      "Email when a canvas document or review artifact is ready to open.",
    testId: "settings-notifications-email-artifact-ready",
  },
  {
    key: "emailProjectActivity",
    label: "Project activity",
    description:
      "Email when you are added to a project or other project membership updates.",
    testId: "settings-notifications-email-project-activity",
  },
];

export function NotificationsSettingsView() {
  const { t } = useTranslation("ui");
  const { notify } = useUI();
  // The STORED toggles, not a schema-filled view of them: a key is present in
  // this object only if the person set it. Rendering falls back to the schema
  // default for an absent key, but the PUT below must not — echoing a default
  // back is what stamps "never asked" into the document as "said no". The
  // server replaces the whole `askPunttNotificationPrefs` object on write, so
  // the payload has to carry every stored key forward; what it must not carry
  // is a key the person never touched.
  const [prefs, setPrefs] = useState<StoredAskPunttNotificationPrefs>({});
  const [loading, setLoading] = useState(true);
  // A set, not a single key: two different rows can have saves in flight at
  // once (a slow save does not disable its siblings). Tracking a lone
  // `savingKey` meant the first save to settle would clear the flag for a
  // *different* row whose PUT was still pending, re-enabling that row's
  // switch mid-flight and letting the user double-submit it.
  const [savingKeys, setSavingKeys] = useState<ReadonlySet<EmailPrefKey>>(
    () => new Set<EmailPrefKey>(),
  );

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const profile = await getUserProfile();
        if (cancelled) return;
        setPrefs(profile.askPunttNotificationPrefs ?? {});
      } catch {
        if (!cancelled) {
          notify({
            variant: "error",
            title: "Could not load notification settings",
          });
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
    // why-not `[notify]`: this is a one-shot load, and `notify` was not a
    // stable reference until the queues in `contexts/ui.tsx` were lazily
    // created — so this effect re-ran on every render of the app provider,
    // dropped the in-flight response on the floor (`cancelled`) and fired
    // another `GET /private/profile`. A late-arriving duplicate could then
    // overwrite a preference the person had just toggled. The status quo is
    // that the list of rows does not depend on the notifier's identity.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const persistPref = useCallback(
    async (key: EmailPrefKey, next: boolean) => {
      const previousValue = prefs[key];
      const nextPrefs = { ...prefs, [key]: next };
      setPrefs(nextPrefs);
      setSavingKeys((current) => new Set(current).add(key));
      try {
        await updateCurrentUserProfile({
          askPunttNotificationPrefs: nextPrefs,
        });
      } catch {
        // why-not: `setPrefs(prefs)` — a whole-object snapshot taken at click
        // time restores keys a sibling toggle already saved successfully, so a
        // failed save would silently discard a change the server accepted.
        setPrefs((current) => ({ ...current, [key]: previousValue }));
        notify({
          variant: "error",
          title: "Could not save notification setting",
        });
      } finally {
        // why-not: `setSavingKeys(new Set())` — clearing the whole set would
        // re-enable a sibling row whose own save is still in flight. Drop only
        // this key so each row's disabled state tracks its own request.
        setSavingKeys((current) => {
          const nextSaving = new Set(current);
          nextSaving.delete(key);
          return nextSaving;
        });
      }
    },
    [notify, prefs],
  );

  return (
    <div
      data-testid="settings-notifications-page"
      className="flex h-full flex-col overflow-hidden"
    >
      <article className="flex-1 space-y-6 overflow-y-auto px-10 py-6">
        <header className="flex flex-col gap-2">
          <Heading as="h1" size="7" weight="bold">
            {t("settings.notificationsSettingsView.notifications")}
          </Heading>
          <Text size="2" color="gray">
            {t("settings.notificationsSettingsView.chooseHowAskPunttAlerts")}
          </Text>
        </header>

        <Card data-testid="settings-notifications-desktop-card">
          <Flex direction="column" gap="3" p="4">
            <Heading as="h2" size="4" weight="medium">
              {t("settings.notificationsSettingsView.desktop")}
            </Heading>
            <Text size="2" color="gray">
              {t(
                "settings.notificationsSettingsView.showBrowserNotificationsWhilePuntt",
              )}
            </Text>
            <AskPunttDesktopNotificationsToggleControl />
          </Flex>
        </Card>

        <Card data-testid="settings-notifications-email-card">
          <Flex direction="column" gap="4" p="4">
            <Flex direction="column" gap="1">
              <Heading as="h2" size="4" weight="medium">
                {t("common:labels.email")}
              </Heading>
              <Text size="2" color="gray">
                Emails are sent from Puntt when these events happen and you are
                not actively reading the chat.
              </Text>
            </Flex>
            {loading ? (
              <Text
                size="2"
                color="gray"
                data-testid="settings-notifications-loading"
              >
                {t("settings.notificationsSettingsView.loadingPreferences")}
              </Text>
            ) : (
              EMAIL_PREF_ROWS.map((row) => (
                <Flex
                  key={row.key}
                  align="center"
                  justify="between"
                  gap="4"
                  data-testid={row.testId}
                >
                  <Flex direction="column" gap="1" style={{ flex: 1 }}>
                    <Text size="2" weight="medium">
                      {row.label}
                    </Text>
                    <Text size="1" color="gray">
                      {row.description}
                    </Text>
                  </Flex>
                  <Switch
                    data-testid={`${row.testId}-switch`}
                    aria-label={row.label}
                    checked={
                      prefs[row.key] ??
                      defaultAskPunttNotificationPrefs[row.key]
                    }
                    disabled={savingKeys.has(row.key)}
                    onCheckedChange={(checked) => {
                      void persistPref(row.key, checked);
                    }}
                  />
                </Flex>
              ))
            )}
          </Flex>
        </Card>
      </article>
    </div>
  );
}
